import { useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { Reorder } from "framer-motion"

import wsClient from "src/wsClient"
import ModalService from "src/modals"
import { selectGame, selectViewerId } from "src/store/game"
import { selectGameConfig } from "src/store/common"
import { ZONE_ORDER, eligibleUnits, zonePriorities } from "src/utils/battlefield"
import { usePointerContext } from "src/utils/input"

/* The team's battle plan: per zone, the unit priorities the engine fills the board with. One
   editor, two homes — inside the live battle view and behind the top bar's FORMACIÓN chip —
   because the plan applies to every battle, so waiting for one to start in order to edit it made
   no sense. Saving sends the whole plan; the server owns it per *team* (any teammate's edit
   applies to everyone), so what comes back on the ack is the shared truth.

   The chips are dragged into order, not clicked: the old click-to-promote could only send a unit
   to the head of its zone, so building any order beyond "this one first" meant clicking the whole
   list in reverse. The keyboard keeps a way in — arrows move the focused chip one place. */

const move = (list, unit, delta) => {
  const from = list.indexOf(unit)
  const to = from + delta
  if (from < 0 || to < 0 || to >= list.length) return list
  const next = [...list]
  next.splice(to, 0, ...next.splice(from, 1))
  return next
}

const FormationEditor = ({ team, onSaved = null }) => {
  const { t } = useTranslation()
  // The verb these hints use: a finger drags, a mouse drags too, but the hint names the gesture.
  const pointerContext = usePointerContext()
  const game = useSelector(selectGame)
  const gameConfig = useSelector(selectGameConfig)
  const userId = useSelector(selectViewerId)
  const [draft, setDraft] = useState(null)

  const current =
    draft ?? game.metadata?.formations?.[team] ?? game.metadata?.users?.[String(userId)]?.formation ?? {}
  const priorities = zonePriorities(gameConfig, team, current)
  const reorder = (zone, order) => setDraft({ ...current, [zone]: order })
  const nudge = (zone, unit, delta) => {
    const order = move(priorities[zone], unit, delta)
    if (order !== priorities[zone]) reorder(zone, order)
  }
  const save = () => {
    wsClient.send("game", { action: "set_formation", formation: draft ?? current })
    onSaved?.()
  }

  return (
    <div className="battle-editor">
      <p className="battle-editor-hint">{t("game.battle.editorHint", { context: pointerContext })}</p>
      {ZONE_ORDER.map((zone) => (
        <div className="battle-editor-zone" key={zone}>
          {/* The zone name is a help trigger (owner's ask, 2026-09-06): pressing it opens the
              zone's card — what standing there means and which units, both sides, may take a slot.
              Same modal the live battle board opens, so the rule is explained in one place. */}
          <button
            type="button"
            className="battle-role-name battle-role-help"
            title={t("game.zoneHelp.open", { zone: t(`game.battle.zones.${zone}`) })}
            onClick={() => ModalService.show("ZoneHelpModal", { zone })}
          >
            {t(`game.battle.zones.${zone}`)}
            <span className="battle-role-help-mark" aria-hidden="true">?</span>
          </button>
          <Reorder.Group
            as="div"
            axis="x"
            className="battle-editor-units"
            values={priorities[zone]}
            onReorder={(order) => reorder(zone, order)}
          >
            {priorities[zone].map((unit, index) => (
              <Reorder.Item
                as="div"
                key={unit}
                value={unit}
                className={`battle-editor-chip${index === 0 ? " first" : ""}`}
                role="button"
                tabIndex={0}
                aria-label={t("game.battle.editorChip", {
                  unit: t(`city.units.${unit}`, { defaultValue: unit }),
                  position: index + 1,
                })}
                onKeyDown={(event) => {
                  if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
                    event.preventDefault()
                    nudge(zone, unit, -1)
                  }
                  if (event.key === "ArrowRight" || event.key === "ArrowDown") {
                    event.preventDefault()
                    nudge(zone, unit, 1)
                  }
                }}
              >
                {index + 1}. {t(`city.units.${unit}`, { defaultValue: unit })}
              </Reorder.Item>
            ))}
            {eligibleUnits(gameConfig, team, zone).length === 0 && <span className="battle-editor-none">—</span>}
          </Reorder.Group>
        </div>
      ))}
      <div className="battle-editor-actions">
        <button type="button" className="quiet" onClick={() => setDraft(null)} disabled={!draft}>
          {t("game.battle.editReset")}
        </button>
        <button type="button" onClick={save} disabled={!draft}>
          {t("game.battle.editSave")}
        </button>
      </div>
    </div>
  )
}

export default FormationEditor
